if (!customElements.get("auto-modal")) {
  class AutoModal extends HTMLElement {
    static getOpenModal() {
      return document.querySelector("auto-modal[open]");
    }

    static getDismissedKey() {
      return "autoModalLastDismissedAt";
    }

    static getSessionStartKey() {
      return "autoModalSessionStartedAt";
    }

    static getConfirmedSuffix() {
      return "-confirmed";
    }

    getConfirmedKey() {
      return `${this.id}${AutoModal.getConfirmedSuffix()}`;
    }

    isConfirmed() {
      if (this.hasAttribute("data-confirmed")) {
        return true;
      }

      try {
        const value = window.localStorage.getItem(this.getConfirmedKey());
        return value === "true" || value === "1";
      } catch (e) {
        return false;
      }
    }

    markConfirmed() {
      this.setAttribute("data-confirmed", "true");
      try {
        window.localStorage.setItem(this.getConfirmedKey(), "true");
      } catch (e) {
        // Ignore localStorage failures
      }
    }

    getSessionStartedAt() {
      try {
        const existing = window.sessionStorage.getItem(
          AutoModal.getSessionStartKey(),
        );
        if (existing) return parseInt(existing, 10);

        const now = Date.now();
        window.sessionStorage.setItem(
          AutoModal.getSessionStartKey(),
          String(now),
        );
        return now;
      } catch (e) {
        return Date.now();
      }
    }

    hasMetExitIntentMinTime() {
      if (this.debug) return true;
      const startedAt = this.getSessionStartedAt();
      return Date.now() - startedAt >= 60 * 1000;
    }

    getLastDismissedAt() {
      try {
        const value = window.sessionStorage.getItem(
          AutoModal.getDismissedKey(),
        );
        return value ? parseInt(value, 10) : 0;
      } catch (e) {
        return 0;
      }
    }

    markDismissed() {
      try {
        window.sessionStorage.setItem(
          AutoModal.getDismissedKey(),
          String(Date.now()),
        );
      } catch (e) {
        // Ignore sessionStorage failures
      }
    }

    isInDismissCooldown() {
      const lastDismissedAt = this.getLastDismissedAt();
      if (!lastDismissedAt) return false;
      return Date.now() - lastDismissedAt < 120 * 1000;
    }

    canOpen({ ignoreDismissCooldown = false } = {}) {
      if (this.isConfirmed()) {
        return false;
      }

      if (!ignoreDismissCooldown && this.isInDismissCooldown()) {
        return false;
      }

      const openModal = AutoModal.getOpenModal();
      return !openModal || openModal === this;
    }

    constructor() {
      super();
      this.autoOpen = !this.hasAttribute("data-closed");
      this.delay = parseInt(this.getAttribute("data-delay"));
      this.debug = this.getAttribute("data-debug");
      this.show = parseInt(this.getAttribute("data-show"));
      this.id = this.getAttribute("id");
      this.trigger = this.getAttribute("data-trigger") || "automatic";
      this.isOpen = false;
      this.exitIntentTriggered = false;

      const self = this;
      document.addEventListener(
        "DOMContentLoaded",
        self.setupButtons.bind(self),
      );

      this.style.setProperty("display", "none");

      this.querySelector('[id^="ModalClose-"]')?.addEventListener(
        "click",
        this.hide.bind(this),
      );
      this.querySelectorAll("[data-close-modal]").forEach((closer) =>
        closer.addEventListener("click", this.hide.bind(this, false)),
      );

      this.addEventListener("keyup", (event) => {
        if (event.code.toUpperCase() === "ESCAPE") this.hide();
      });
      this.addEventListener("click", (event) => {
        if (event.target.nodeName === "AUTO-MODAL") this.hide();
      });

      if (this.autoOpen) {
        if (this.trigger === "exit_intent") {
          this.setupExitIntent();
        } else {
          window.setTimeout(self.tryToOpen.bind(self), this.delay * 1000);
        }
      }
    }

    setupExitIntent() {
      const self = this;

      // Establish session start for cross-page gating
      self.getSessionStartedAt();

      // Only trigger on desktop (mouse-based devices)
      if (window.matchMedia("(pointer: fine)").matches) {
        document.addEventListener("mouseout", function handleMouseOut(e) {
          // Check if mouse is leaving viewport at the top
          if (
            e.clientY <= 0 &&
            self.hasMetExitIntentMinTime() &&
            !self.exitIntentTriggered &&
            !self.isOpen
          ) {
            self.exitIntentTriggered = true;
            self.tryToOpen();
          }
        });
      } else {
        // Fallback for touch devices: use scroll up detection or delayed trigger
        let lastScrollY = window.scrollY;
        let scrollUpCount = 0;

        window.addEventListener("scroll", function handleScroll() {
          if (window.scrollY < lastScrollY) {
            scrollUpCount++;
            // Trigger after scrolling up significantly from lower in the page
            if (
              self.hasMetExitIntentMinTime() &&
              scrollUpCount > 3 &&
              lastScrollY > window.innerHeight &&
              !self.exitIntentTriggered &&
              !self.isOpen
            ) {
              self.exitIntentTriggered = true;
              self.tryToOpen();
            }
          } else {
            scrollUpCount = 0;
          }
          lastScrollY = window.scrollY;
        });
      }
    }

    setupButtons() {
      const now = new Date();
      const self = this;

      const openers = document.querySelectorAll(
        '[id^="ModalOpen-' + this.id + '"],' + '[data-open="' + this.id + '"]',
      );
      if (openers) {
        const self = this;
        openers.forEach((el) => {
          el.addEventListener("click", self.open.bind(this));
        });
      }
      document
        .querySelectorAll(`[data-dismiss-open="${this.id}"]`)
        .forEach((el) =>
          el.addEventListener("click", self.dismissOpenButton.bind(self)),
        );

      let shouldOpen = true;
      if (this.isConfirmed()) {
        shouldOpen = false;
      }

      if (localStorage.getItem("popupButtonDismissed-" + this.id)) {
        if (
          parseInt(localStorage.getItem("popupButtonDismissed-" + this.id)) <=
          now.getTime()
        ) {
          shouldOpen = false;
        }
      }
      if (shouldOpen) {
        document
          .querySelectorAll('[data-open="' + this.id + '"]')
          .forEach((el) => {
            el.parentElement.classList.add("active");
            el.parentElement.setAttribute("tabindex", "0");
          });
      }
    }

    dismissOpenButton() {
      const now = new Date();

      localStorage.setItem(
        "popupButtonDismissed-" + this.id,
        now.getTime() + 6000,
      );

      document
        .querySelectorAll('[data-open="' + this.id + '"][data-dismissable]')
        .forEach((el) => {
          el.parentElement.classList.add("dismissed");
          el.parentElement.setAttribute("tabindex", "-1");
          el.setAttribute("disabled", "true");
        });
    }

    open() {
      if (!this.canOpen({ ignoreDismissCooldown: true })) {
        return;
      }
      this.style.removeProperty("display");
      if (this.isOpen) {
        return;
      }
      this.isOpen = true;
      document.body.classList.add("overflow-hidden");
      this.setAttribute("open", "");
      trapFocus(this, this.querySelector(".modal-content"));
      // window.pauseAllMedia();
      this.setStorage();
    }

    hide() {
      const wasOpen = this.isOpen;
      this.isOpen = false;
      document.body.classList.remove("overflow-hidden");
      this.removeAttribute("open");
      // window.pauseAllMedia();

      if (wasOpen) {
        this.markDismissed();
      }

      setTimeout(() => {
        this.style.setProperty("display", "none");
      }, 1000);
    }

    tryToOpen() {
      if (this.isConfirmed()) {
        return;
      }

      if (!this.canOpen()) {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      if (window.IS_FS_SPLASH === true) {
        //Don't open on splash pages
        return;
      }

      if (params.has("openPopups")) {
        this.open();
        return;
      }
      if (this.debug) {
        this.open();
        return;
      }

      if (this.isOpen) {
        return;
      }

      if (this.show > 0) {
        const now = new Date();
        if (localStorage.getItem(this.id)) {
          if (parseInt(localStorage.getItem(this.id)) <= now.getTime()) {
            this.open();
          }
        } else {
          this.open();
        }
      } else {
        if (!window.sessionStorage.getItem(this.id) !== "shown") {
          this.open();
          window.sessionStorage.setItem(this.id, "shown");
        }
      }
    }

    setStorage() {
      const now = new Date();
      localStorage.setItem(this.id, now.getTime() + this.show * 60 * 60 * 1000);
    }
  }

  customElements.define("auto-modal", AutoModal);
}
